import React, { useEffect, useState } from "react";

export function usePaginatedUsers(fetcher) {
  const [items, setItems] = useState([]);
  const [view, setView] = useState(null);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const [loading, setLoading] = useState(false);

  const load = async (opts) => {
    setLoading(true);
    try {
      const data = await fetcher(opts);
      setItems(data.items);
      setView(data.view);
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load({ filters, sort });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort]);

  const runSearch = (e) => {
    e.preventDefault();
    load({ filters, sort });
  };

  const goTo = (url) => {
    if (url) load({ url });
  };

  const toggleSort = (key) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  };

  return { items, setItems, view, total, filters, setFilters, sort, toggleSort, runSearch, goTo, loading };
}

function pageNumber(url) {
  if (!url) return null;
  const m = url.match(/[?&]page=(\d+)/);
  return m ? Number(m[1]) : 1;
}

function withPage(url, page) {
  if (!url) return null;
  return /[?&]page=\d+/.test(url)
    ? url.replace(/([?&]page=)\d+/, `$1${page}`)
    : url + (url.includes("?") ? "&" : "?") + `page=${page}`;
}

// Fenêtre de numéros de page autour de la page courante (même logique que
// la liste de suggestions)
function pageWindow(current, total) {
  const start = Math.max(1, current - 2);
  const end = Math.min(total, start + 4);
  const realStart = Math.max(1, end - 4);
  const pages = [];
  for (let p = realStart; p <= end; p++) pages.push(p);
  return pages;
}

export default function UserTable({ title, columns, state, rowKey, expandedRowKey, renderExpandedRow, searchFields = [], sortControls = [], headerActions }) {
  const { items, view, total, filters, setFilters, sort, toggleSort, runSearch, goTo, loading } = state;

  const setFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const currentPage = pageNumber(view?.["@id"]) ?? 1;
  const totalPages = pageNumber(view?.last) ?? currentPage;

  return (
    <div className="admin-page">
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">{title}</h3>
          <div className="card-actions d-flex align-items-center flex-wrap gap-2">
            {headerActions}
            <span className="text-secondary">
              {total} utilisateur{total > 1 ? "s" : ""}
            </span>
            <form onSubmit={runSearch} style={{ display: "flex", gap: 8 }}>
              {searchFields.map((f) => (
                <input
                  key={f.key}
                  type="text"
                  className="form-control"
                  placeholder={f.placeholder}
                  value={filters[f.key] || ""}
                  onChange={(e) => setFilter(f.key, e.target.value)}
                />
              ))}
              <button type="submit" className="fr-btn--secondary">Rechercher</button>
            </form>
            {sortControls.map((s) => (
              <button
                key={s.key}
                type="button"
                className="fr-btn--secondary"
                onClick={() => toggleSort(s.key)}
              >
                Trier par {s.label}
                {sort.key === s.key ? (sort.dir === "asc" ? " ▲" : " ▼") : " ↕"}
              </button>
            ))}
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-vcenter" style={{ width: "100%" }}>
            <thead>
              <tr>
                {columns.map((c) => (
                  <th
                    key={c.key}
                    className="admin-table-th"
                    style={c.sortKey ? { cursor: "pointer", userSelect: "none" } : undefined}
                    onClick={c.sortKey ? () => toggleSort(c.sortKey) : undefined}
                  >
                    {c.label}
                    {c.sortKey && (sort.key === c.sortKey ? (sort.dir === "asc" ? " ▲" : " ▼") : " ↕")}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!loading && items.map((item) => (
                <React.Fragment key={rowKey(item)}>
                  <tr className="admin-table-row">
                    {columns.map((c) => (
                      <td key={c.key} className="admin-table-td">{c.render ? c.render(item) : (item[c.key] ?? "—")}</td>
                    ))}
                  </tr>
                  {renderExpandedRow && expandedRowKey === rowKey(item) && (
                    <tr>
                      <td colSpan={columns.length} style={{ padding: 0 }}>
                        {renderExpandedRow(item)}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {view && (
          <div className="card-footer d-flex align-items-center justify-content-end gap-2">
            <span className="text-secondary">Page {currentPage} / {totalPages}</span>
            <button
              className="fr-btn--secondary"
              disabled={!view["hydra:previous"] && !view.previous}
              onClick={() => goTo(view["hydra:previous"] || view.previous)}
            >
              ◀ Précédent
            </button>
            {pageWindow(currentPage, totalPages).map((p) => (
              <button
                key={p}
                className="fr-btn--secondary"
                disabled={p === currentPage}
                style={p === currentPage ? { fontWeight: 700 } : undefined}
                onClick={() => goTo(withPage(view["@id"], p))}
              >
                {p}
              </button>
            ))}
            <button
              className="fr-btn--secondary"
              disabled={!view["hydra:next"] && !view.next}
              onClick={() => goTo(view["hydra:next"] || view.next)}
            >
              Suivant ▶
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
